/**
 * @module NotificationPanel
 * @package @geenius/notifications-react-css
 * @description Renders the compact notification drawer used by the React
 * vanilla CSS variant.
 */

import type { Notification } from '@geenius/notifications/shared'
import type { JSX, KeyboardEvent as ReactKeyboardEvent } from 'react'
import { useEffect, useRef } from 'react'
import { cx } from '../lib/cx'
import { useNotificationCopy } from '../lib/notificationCopy'
import { EmptyNotifications } from './EmptyNotifications'
import { NotificationItem } from './NotificationItem'

const FOCUSABLE_SELECTORS = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

function getFocusableElements(container: HTMLElement): HTMLElement[] {
  return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTORS))
    .filter((element) => element.getAttribute('aria-hidden') !== 'true')
}

/**
 * Props for the React CSS notification panel.
 */
export interface NotificationPanelProps {
  notifications: Notification[]
  onMarkAll?: () => void
  onClose?: () => void
  onRead?: (id: string) => void
  onDismiss?: (id: string) => void
  onViewAll?: () => void
  panelId?: string
  title?: string
  className?: string
}

/**
 * Renders the notification drawer panel.
 *
 * @param props Drawer props including notification data and action handlers.
 * @returns The drawer panel markup.
 */
export function NotificationPanel({
  notifications,
  onMarkAll,
  onClose,
  onRead,
  onDismiss,
  onViewAll,
  panelId = 'notifications-panel',
  title,
  className,
}: NotificationPanelProps): JSX.Element {
  const { t } = useNotificationCopy()
  const panelRef = useRef<HTMLElement | null>(null)
  const unreadCount = notifications.filter(
    (notification) => notification.status === 'unread',
  ).length
  const titleId = `${panelId}-title`

  useEffect(() => {
    const panel = panelRef.current

    if (!panel) {
      return
    }

    const previousFocus =
      document.activeElement instanceof HTMLElement ? document.activeElement : null
    const focusable = getFocusableElements(panel)

    ;(focusable[0] ?? panel).focus()

    return () => {
      previousFocus?.focus()
    }
  }, [])

  const handleKeyDown = (event: ReactKeyboardEvent<HTMLElement>): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      onClose?.()
      return
    }

    if (event.key !== 'Tab') {
      return
    }

    const panel = panelRef.current

    if (!panel) {
      return
    }

    const focusable = getFocusableElements(panel)

    if (focusable.length === 0) {
      event.preventDefault()
      panel.focus()
      return
    }

    const firstFocusable = focusable[0]
    const lastFocusable = focusable[focusable.length - 1]
    const activeElement = document.activeElement

    if (event.shiftKey) {
      if (activeElement === firstFocusable || activeElement === panel) {
        event.preventDefault()
        lastFocusable.focus()
      }
      return
    }

    if (activeElement === lastFocusable) {
      event.preventDefault()
      firstFocusable.focus()
    }
  }

  return (
    <section
      ref={panelRef}
      id={panelId}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      tabIndex={-1}
      onKeyDown={handleKeyDown}
      className={cx('notifications__panel', 'gm-motion', 'gm-motion--scale-in', className)}
    >
      <div className={cx('notifications__panel-header')}>
        <span id={titleId} className={cx('notifications__panel-title')}>
          {title ?? t('notifications.common.title')}
          <span className={cx('notifications__badge', 'notifications__badge--large')}>
            {unreadCount}
          </span>
        </span>
        <div className={cx('notifications__panel-actions')}>
          <button
            type="button"
            className={cx('notifications__panel-mark-all')}
            onClick={onMarkAll}
            disabled={!onMarkAll || unreadCount === 0}
          >
            {t('notifications.common.markAllRead')}
          </button>
          <button
            type="button"
            className={cx('notifications__panel-close')}
            onClick={onClose}
            aria-label={t('notifications.panel.closeAria')}
          >
            ✕
          </button>
        </div>
      </div>
      <div className={cx('notifications__panel-body')}>
        {notifications.length === 0 ? (
          <EmptyNotifications />
        ) : (
          <ul className={cx('notifications__panel-list')}>
            {notifications.map((notification) => (
              <li key={notification.id}>
                <NotificationItem
                  notification={notification}
                  onRead={onRead}
                  onDismiss={onDismiss}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
      {notifications.length > 0 && onViewAll ? (
        <div className={cx('notifications__panel-footer')}>
          <button
            type="button"
            className={cx('notifications__panel-view-all')}
            onClick={onViewAll}
          >
            {t('notifications.common.viewAll')}
          </button>
        </div>
      ) : null}
    </section>
  )
}
